import type { BoatIncome } from '@sea-snag/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sea-snag/ui';
import { Check, Loader2 } from 'lucide-react';
import { useState } from 'react';

interface PerBoatIncomeTableProps {
  boats: BoatIncome[];
  currency: string;
  year: number;
  canEdit: boolean;
  isPending: boolean;
  onSaveShare: (boatPid: string, sharePct: number) => void;
}

function fmt(currency: string, value: number | null): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

export function PerBoatIncomeTable({
  boats,
  currency,
  year,
  canEdit,
  isPending,
  onSaveShare,
}: PerBoatIncomeTableProps) {
  // Per-boat in-progress edits (string while typing). Absent ⇒ not editing.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const setDraft = (boatPid: string, value: string) =>
    setDrafts((prev) => ({ ...prev, [boatPid]: value }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Per-boat income · {year}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Boat</TableHead>
              <TableHead className="text-right">Bookings</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">Tax</TableHead>
              <TableHead className="text-right">Net</TableHead>
              <TableHead className="text-right">Share %</TableHead>
              <TableHead className="text-right">Owner</TableHead>
              <TableHead className="text-right">Management</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {boats.map((b) => {
              const draft = drafts[b.boatPid];
              const current = b.sharePct ?? 0;
              const draftNum = draft !== undefined ? Number(draft) : current;
              const dirty = draft !== undefined && draftNum !== current;
              return (
                <TableRow key={b.boatPid}>
                  <TableCell>
                    <div className="font-medium">{b.name}</div>
                    {b.model && <div className="text-xs text-muted-foreground">{b.model}</div>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{b.bookings}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmt(currency, b.gross)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    −{fmt(currency, b.tax)}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {fmt(currency, b.net)}
                  </TableCell>
                  <TableCell className="text-right">
                    {canEdit ? (
                      <div className="inline-flex items-center gap-1">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          aria-label={`Owner share for ${b.name}`}
                          className="h-7 w-16 text-right text-xs"
                          value={draft ?? (b.sharePct != null ? String(b.sharePct) : '')}
                          placeholder="—"
                          onChange={(e) => setDraft(b.boatPid, e.target.value)}
                        />
                        <span className="text-xs text-muted-foreground">%</span>
                        {dirty && (
                          <Button
                            size="sm"
                            className="ml-1 h-7 px-2 text-xs"
                            disabled={isPending}
                            aria-label={`Save share for ${b.name}`}
                            onClick={() => {
                              const clamped = Math.max(0, Math.min(100, draftNum));
                              onSaveShare(b.boatPid, clamped);
                              setDrafts((prev) => {
                                const next = { ...prev };
                                delete next[b.boatPid];
                                return next;
                              });
                            }}
                          >
                            {isPending ? (
                              <Loader2 className="size-3 animate-spin" />
                            ) : (
                              <Check className="size-3" />
                            )}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <span className="tabular-nums">
                        {b.sharePct != null ? `${b.sharePct}%` : '—'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmt(currency, b.ownerIncome)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmt(currency, b.managementIncome)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
